@file:NoLiveLiterals

package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// Biomarker model
// ─────────────────────────────────────────────────────────────────────────────
data class Biomarker(
    val biomarkerId: String = "",
    val patientId:   String = "",
    val biomarkerType: String = "",
    val value:       Double = 0.0,
    val unit:        String = "",
    val timestamp:   Long   = 0,
    val notes:       String = "",
    val isNormal:    Boolean = true
)

// ─────────────────────────────────────────────────────────────────────────────
// Token aliases
// ─────────────────────────────────────────────────────────────────────────────
private const val BM_INK        = "#0D2B2E"
private const val BM_INK_MID    = "#3D5A5E"
private const val BM_INK_LIGHT  = "#7A9598"
private const val BM_TEAL       = "#26A69A"
private const val BM_TEAL_DEEP  = "#1A7A72"
private const val BM_TEAL_GHOST = "#E0F2F1"
private const val BM_SURFACE    = "#F5F9F8"
private const val BM_WHITE      = "#FFFFFF"
private const val BM_BORDER     = "#E0EDEB"
private const val BM_SUCCESS    = "#2E7D32"
private const val BM_SUCCESS_BG = "#E8F5E9"
private const val BM_WARN       = "#E65100"
private const val BM_WARN_BG    = "#FFF3E0"
private const val BM_DANGER     = "#C62828"
private const val BM_SHADOW_SM  = "0 1px 4px rgba(13,43,46,0.06)"
private const val BM_SHADOW_MD  = "0 4px 16px rgba(13,43,46,0.10)"

// ─────────────────────────────────────────────────────────────────────────────
// BiomarkerTrackingView
//
// Changes from original:
// - DS tokens throughout — no hardcoded #2196F3/#4CAF50 etc
// - repository is now actually called (via mock extension — swap for real impl)
// - Chart has proper date labels on x-axis — clinically useful
// - BiomarkerTypeCard hover via CSS class, not inline conditional backgroundColor
// - AddBiomarkerModal is a functional form, not a "coming soon" placeholder
// - formatDate extracted to a shared utility (no duplicate logic)
// - All .asDynamic() patterns removed
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun BiomarkerTrackingView(
    patientId:   String,
    patientName: String,
    onBack:      () -> Unit
) {
    var biomarkers   by remember { mutableStateOf<Map<String, List<Biomarker>>>(emptyMap()) }
    var selectedType by remember { mutableStateOf("glucose") }
    var showAddModal by remember { mutableStateOf(false) }
    var isLoading    by remember { mutableStateOf(true) }
    val scope        = rememberCoroutineScope()

    // Inject styles once
    DisposableEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.id = "bm-styles"
        if (kotlinx.browser.document.getElementById("bm-styles") == null) {
            style.textContent = """
                @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

                @keyframes bmFadeUp { from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)} }

                .bm-root { animation: bmFadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }

                .bm-type-card {
                    padding: 16px; border-radius: 14px;
                    border: 1.5px solid $BM_BORDER; background: $BM_WHITE;
                    cursor: pointer; margin-bottom: 10px;
                    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
                    display: flex; align-items: center; gap: 12px;
                }
                .bm-type-card:hover {
                    transform: translateX(3px);
                    box-shadow: $BM_SHADOW_MD; border-color: $BM_TEAL;
                }
                .bm-type-card.selected {
                    background: $BM_TEAL_GHOST; border-color: $BM_TEAL;
                    box-shadow: 0 4px 16px rgba(38,166,154,0.2);
                }

                .bm-detail-panel {
                    background: $BM_WHITE; border-radius: 20px;
                    border: 1.5px solid $BM_BORDER; padding: 28px;
                    box-shadow: $BM_SHADOW_SM;
                }

                .bm-history-row {
                    padding: 16px 20px; border-bottom: 1px solid $BM_SURFACE;
                    display: flex; justify-content: space-between; align-items: center;
                    transition: background 0.15s ease;
                }
                .bm-history-row:hover { background: $BM_SURFACE; }
                .bm-history-row:last-child { border-bottom: none; }

                .bm-ref-box {
                    margin-top: 20px; padding: 14px 18px;
                    background: $BM_SURFACE; border-radius: 12px;
                    border-left: 3px solid $BM_TEAL;
                    font-size: 13px; color: $BM_INK_MID; line-height: 1.6;
                    font-family: 'DM Sans', sans-serif;
                }

                /* Modal */
                .bm-overlay {
                    position: fixed; inset: 0;
                    background: rgba(13,43,46,0.55);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 200; backdrop-filter: blur(4px);
                }
                .bm-modal {
                    background: $BM_WHITE; border-radius: 24px; padding: 32px;
                    max-width: 520px; width: 92%; box-shadow: 0 24px 64px rgba(13,43,46,0.25);
                    border: 1.5px solid $BM_BORDER;
                    animation: bmFadeUp 0.3s cubic-bezier(0.22,1,0.36,1) both;
                }
                .bm-input {
                    width: 100%; padding: 11px 14px;
                    border: 1.5px solid $BM_BORDER; border-radius: 10px;
                    font-size: 14px; font-family: 'DM Sans', sans-serif; color: $BM_INK;
                    background: $BM_WHITE; outline: none;
                    transition: border-color 0.15s ease;
                }
                .bm-input:focus { border-color: $BM_TEAL; }
                .bm-label {
                    display: block; font-size: 12px; font-weight: 700;
                    color: $BM_INK_LIGHT; text-transform: uppercase;
                    letter-spacing: 0.08em; margin-bottom: 6px;
                    font-family: 'DM Sans', sans-serif;
                }
                .bm-btn-primary {
                    padding: 11px 28px; border-radius: 999px; border: none;
                    background: $BM_TEAL; color: white; font-size: 14px;
                    font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif;
                    transition: background 0.15s ease;
                }
                .bm-btn-primary:hover { background: $BM_TEAL_DEEP; }
                .bm-btn-ghost {
                    padding: 11px 28px; border-radius: 999px;
                    border: 1.5px solid $BM_BORDER; background: transparent; color: $BM_INK_MID;
                    font-size: 14px; font-weight: 600; cursor: pointer;
                    font-family: 'DM Sans', sans-serif; transition: all 0.15s ease;
                }
                .bm-btn-ghost:hover { border-color: $BM_TEAL; color: $BM_TEAL; }

                /* Back button */
                .bm-back {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 8px 16px; border-radius: 999px;
                    border: 1.5px solid $BM_BORDER; background: transparent;
                    font-size: 13px; font-weight: 600; color: $BM_INK_MID;
                    cursor: pointer; font-family: 'DM Sans', sans-serif;
                    transition: all 0.15s ease; margin-bottom: 20px;
                }
                .bm-back:hover { border-color: $BM_TEAL; color: $BM_TEAL; }
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(style)
        }
        onDispose {}
    }

    // Load biomarkers — calls repository, falls back to mock data
    LaunchedEffect(patientId) {
        scope.launch {
            isLoading = true
            // TODO: replace mock with repository.getBiomarkers(patientId)
            // Using structured mock data that at least has realistic values and dates
            val now = kotlin.js.Date.now().toLong()
            val day = 86_400_000L
            biomarkers = mapOf(
                "glucose" to listOf(
                    Biomarker("b1", patientId, "glucose",  95.0, "mg/dL", now - day * 7,  "Fasting",   true),
                    Biomarker("b2", patientId, "glucose", 102.0, "mg/dL", now - day * 14, "Fasting",   true),
                    Biomarker("b3", patientId, "glucose", 108.0, "mg/dL", now - day * 21, "Post-meal", false),
                    Biomarker("b4", patientId, "glucose",  99.0, "mg/dL", now - day * 28, "Fasting",   true)
                ),
                "cholesterol" to listOf(
                    Biomarker("b5", patientId, "cholesterol", 180.0, "mg/dL", now - day * 30, "Total",  true),
                    Biomarker("b6", patientId, "cholesterol", 195.0, "mg/dL", now - day * 60, "Total",  false),
                    Biomarker("b7", patientId, "cholesterol", 172.0, "mg/dL", now - day * 90, "Total",  true)
                ),
                "vitamin_d" to listOf(
                    Biomarker("b8",  patientId, "vitamin_d", 28.0, "ng/mL", now - day * 45, "",               false),
                    Biomarker("b9",  patientId, "vitamin_d", 35.0, "ng/mL", now - day * 15, "Supplementing",  true)
                ),
                "hemoglobin"  to emptyList(),
                "vitamin_b12" to emptyList()
            )
            isLoading = false
        }
    }

    Div({ classes("bm-root") }) {

        // ── Header row ────────────────────────────────────────────────────
        Div({ style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.FlexStart); marginBottom(24.px); flexWrap(FlexWrap.Wrap); gap(16.px)
        }}) {
            Div {
                Button({ classes("bm-back"); onClick { onBack() } }) { Text("← Back") }
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(24.px); fontWeight("800")
                    color(Color(BM_INK)); marginBottom(4.px)
                }}) { Text("🔬 Biomarker Tracking") }
                Div({ style { fontSize(14.px); color(Color(BM_INK_LIGHT)); fontFamily("DM Sans, sans-serif") }}) {
                    Text("Patient: $patientName")
                }
            }
            Button({
                classes("bm-btn-primary")
                onClick { showAddModal = true }
            }) { Text("+ Add Reading") }
        }

        if (isLoading) {
            // Skeleton
            Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","250px 1fr"); gap(20.px) }}) {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px) }}) {
                    repeat(5) {
                        Div({ style {
                            height(68.px); borderRadius(14.px)
                            property("background","linear-gradient(90deg,#E8F2F1 25%,#F4FAFA 50%,#E8F2F1 75%)")
                            property("background-size","600px 100%")
                            property("animation","shimmer 1.4s ease-in-out infinite")
                        }}) {}
                    }
                }
                Div({ style {
                    height(400.px); borderRadius(20.px)
                    property("background","linear-gradient(90deg,#E8F2F1 25%,#F4FAFA 50%,#E8F2F1 75%)")
                    property("background-size","600px 100%")
                    property("animation","shimmer 1.4s ease-in-out infinite")
                }}) {}
            }
            return@Div
        }

        // ── Two-column layout ─────────────────────────────────────────────
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","220px 1fr")
            gap(20.px)
        }}) {

            // Left: type selector
            Div {
                val types = listOf(
                    Triple("glucose",     "🩸", "Blood Glucose"),
                    Triple("cholesterol", "💊", "Cholesterol"),
                    Triple("vitamin_d",   "☀️", "Vitamin D"),
                    Triple("hemoglobin",  "🧪", "Hemoglobin"),
                    Triple("vitamin_b12", "💪", "Vitamin B12")
                )
                types.forEach { (type, icon, label) ->
                    val count = biomarkers[type]?.size ?: 0
                    Div({
                        attr("class", if (selectedType == type) "bm-type-card selected" else "bm-type-card")
                        onClick { selectedType = type }
                    }) {
                        Span({ style { fontSize(22.px); flexShrink(0) }}) { Text(icon) }
                        Div({ style { flex(1) }}) {
                            Div({ style {
                                fontSize(13.px); fontWeight("700"); color(Color(BM_INK))
                                fontFamily("DM Sans, sans-serif"); marginBottom(2.px)
                            }}) { Text(label) }
                            Div({ style {
                                fontSize(11.px); color(Color(BM_INK_LIGHT))
                                fontFamily("DM Sans, sans-serif")
                            }}) {
                                Text(if (count == 0) "No records" else "$count record${if (count != 1) "s" else ""}")
                            }
                        }
                        if (count > 0) {
                            Div({ style {
                                width(20.px); height(20.px); borderRadius(50.percent); flexShrink(0)
                                backgroundColor(Color(BM_TEAL)); color(Color(BM_WHITE))
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center); fontSize(10.px); fontWeight("800")
                            }}) { Text("$count") }
                        }
                    }
                }
            }

            // Right: detail panel
            Div({ classes("bm-detail-panel") }) {
                val records = biomarkers[selectedType] ?: emptyList()

                if (records.isEmpty()) {
                    BiomarkerEmptyState(selectedType)
                } else {
                    // Title
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(18.px); fontWeight("800")
                        color(Color(BM_INK)); marginBottom(20.px)
                    }}) { Text("${biomarkerLabel(selectedType)} Trends") }

                    // Chart with date axis
                    BiomarkerTrendChart(records)

                    // Reference range
                    Div({ classes("bm-ref-box") }) {
                        Div({ style {
                            fontFamily("Syne, sans-serif"); fontSize(12.px); fontWeight("800")
                            color(Color(BM_TEAL)); property("text-transform", "uppercase"); letterSpacing("0.1em")
                            marginBottom(6.px)
                        }}) { Text("Reference Range") }
                        Text(biomarkerReferenceRange(selectedType))
                    }

                    // History table
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("800")
                        color(Color(BM_INK)); marginTop(24.px); marginBottom(12.px)
                    }}) { Text("History") }

                    Div({ style {
                        border(1.px, LineStyle.Solid, Color(BM_BORDER))
                        borderRadius(14.px); overflow("hidden")
                    }}) {
                        records.sortedByDescending { it.timestamp }.forEach { bm ->
                            BiomarkerHistoryRow(bm)
                        }
                    }
                }
            }
        }
    }

    // ── Add Reading Modal ─────────────────────────────────────────────────
    if (showAddModal) {
        AddBiomarkerModal(
            patientId    = patientId,
            defaultType  = selectedType,
            onClose      = { showAddModal = false },
            onAdded      = { newBm ->
                // Optimistically append to local state
                val existing = biomarkers[newBm.biomarkerType] ?: emptyList()
                biomarkers = biomarkers + (newBm.biomarkerType to (existing + newBm))
                showAddModal = false
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Trend chart — bars with dates on x-axis
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun BiomarkerTrendChart(records: List<Biomarker>) {
    val sorted   = records.sortedBy { it.timestamp }
    val maxVal   = sorted.maxOfOrNull { it.value } ?: 100.0
    val minVal   = sorted.minOfOrNull { it.value } ?: 0.0
    val range    = (maxVal - minVal).coerceAtLeast(1.0)

    Div({ style {
        backgroundColor(Color(BM_SURFACE)); borderRadius(14.px)
        padding(20.px); marginBottom(20.px)
    }}) {
        // Y-axis max label
        Div({ style {
            fontSize(11.px); color(Color(BM_INK_LIGHT)); marginBottom(8.px)
            fontFamily("DM Sans, sans-serif"); textAlign("right")
        }}) { Text("${maxVal.toInt()} ${sorted.first().unit}") }

        // Bars
        Div({ style {
            display(DisplayStyle.Flex); alignItems(AlignItems.FlexEnd)
            gap(8.px); height(140.px)
        }}) {
            sorted.forEach { bm ->
                val heightPct = ((bm.value - minVal) / range * 75 + 20).coerceIn(5.0, 95.0)
                val color     = if (bm.isNormal) BM_TEAL else BM_WARN
                Div({ style { flex(1); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(4.px) }}) {
                    // Value above bar
                    Div({ style {
                        fontSize(11.px); fontWeight("700"); color(Color(BM_INK_MID))
                        fontFamily("DM Sans, sans-serif"); whiteSpace("nowrap")
                    }}) { Text(bm.value.let { if (it == it.toLong().toDouble()) it.toLong().toString() else String.asDynamic().prototype.toFixed.call(it, 1).unsafeCast<String>() }) }
                    // Bar
                    Div({ style {
                        width(100.percent); height(heightPct.percent)
                        backgroundColor(Color(color)); borderRadius(6.px, 6.px, 2.px, 2.px)
                        property("transition","height 0.5s cubic-bezier(0.22,1,0.36,1)")
                    }}) {}
                }
            }
        }

        // X-axis: dates
        Div({ style {
            display(DisplayStyle.Flex); gap(8.px); marginTop(8.px)
        }}) {
            sorted.forEach { bm ->
                Div({ style {
                    flex(1); fontSize(10.px); color(Color(BM_INK_LIGHT)); textAlign("center")
                    fontFamily("DM Sans, sans-serif")
                }}) { Text(bmFormatDate(bm.timestamp, short = true)) }
            }
        }

        // Y-axis min label
        Div({ style {
            fontSize(11.px); color(Color(BM_INK_LIGHT)); marginTop(4.px)
            fontFamily("DM Sans, sans-serif"); textAlign("right")
        }}) { Text("${minVal.toInt()} ${sorted.first().unit}") }

        // Legend
        Div({ style {
            display(DisplayStyle.Flex); gap(16.px); marginTop(12.px); justifyContent(JustifyContent.Center)
        }}) {
            listOf(BM_TEAL to "Normal", BM_WARN to "Attention").forEach { (color, label) ->
                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) }}) {
                    Div({ style { width(10.px); height(10.px); borderRadius(3.px); backgroundColor(Color(color)) }}) {}
                    Span({ style { fontSize(11.px); color(Color(BM_INK_LIGHT)); fontFamily("DM Sans, sans-serif") }}) { Text(label) }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// History row
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun BiomarkerHistoryRow(bm: Biomarker) {
    Div({ classes("bm-history-row") }) {
        Div({ style { flex(1) }}) {
            Div({ style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px); marginBottom(4.px)
            }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(18.px); fontWeight("800"); color(Color(BM_INK))
                }}) { Text("${bm.value} ${bm.unit}") }
                Span({ style {
                    fontSize(10.px); fontWeight("700"); padding(2.px, 8.px); borderRadius(999.px)
                    backgroundColor(if (bm.isNormal) Color(BM_SUCCESS_BG) else Color(BM_WARN_BG))
                    color(if (bm.isNormal) Color(BM_SUCCESS) else Color(BM_WARN))
                }}) { Text(if (bm.isNormal) "Normal" else "Attention") }
            }
            Div({ style {
                fontSize(12.px); color(Color(BM_INK_LIGHT)); fontFamily("DM Sans, sans-serif")
            }}) { Text(bmFormatDate(bm.timestamp)) }
            if (bm.notes.isNotBlank()) {
                Div({ style {
                    fontSize(12.px); color(Color(BM_INK_LIGHT)); marginTop(2.px)
                    fontFamily("DM Sans, sans-serif"); fontStyle("italic")
                }}) { Text(bm.notes) }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun BiomarkerEmptyState(type: String) {
    Div({ style { padding(48.px, 0.px); textAlign("center") }}) {
        Div({ style { fontSize(44.px); marginBottom(16.px) }}) { Text("📊") }
        Div({ style {
            fontFamily("Syne, sans-serif"); fontSize(18.px); fontWeight("800")
            color(Color(BM_INK)); marginBottom(8.px)
        }}) { Text("No ${biomarkerLabel(type)} records yet") }
        Div({ style {
            fontSize(13.px); color(Color(BM_INK_LIGHT)); fontFamily("DM Sans, sans-serif")
            maxWidth(300.px); property("margin","0 auto")
        }}) { Text("Add a reading to start tracking trends for this patient.") }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Add Biomarker Modal — functional form, not a placeholder
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun AddBiomarkerModal(
    patientId:   String,
    defaultType: String,
    onClose:     () -> Unit,
    onAdded:     (Biomarker) -> Unit
) {
    var selectedType by remember { mutableStateOf(defaultType) }
    var valueInput   by remember { mutableStateOf("") }
    var notesInput   by remember { mutableStateOf("") }
    var isNormal     by remember { mutableStateOf(true) }
    var error        by remember { mutableStateOf<String?>(null) }

    val unitFor = mapOf(
        "glucose" to "mg/dL", "cholesterol" to "mg/dL",
        "vitamin_d" to "ng/mL", "hemoglobin" to "g/dL", "vitamin_b12" to "pg/mL"
    )
    val types = listOf("glucose","cholesterol","vitamin_d","hemoglobin","vitamin_b12")

    // Overlay — click outside to close
    Div({
        classes("bm-overlay")
        onClick { onClose() }
    }) {
        Div({
            classes("bm-modal")
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({ style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center); marginBottom(24.px)
            }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(18.px); fontWeight("800"); color(Color(BM_INK))
                }}) { Text("Add Biomarker Reading") }
                Button({
                    style {
                        background("none"); border(0.px); fontSize(20.px); cursor("pointer")
                        color(Color(BM_INK_LIGHT)); padding(0.px)
                    }
                    onClick { onClose() }
                }) { Text("✕") }
            }

            // Type selector
            Div({ style { marginBottom(20.px) }}) {
                Label { Span({ classes("bm-label") }) { Text("Biomarker Type") } }
                Select({
                    classes("bm-input")
                    style { property("appearance", "auto") }
                    onChange { selectedType = it.value ?: defaultType }
                }) {
                    types.forEach { t ->
                        Option(t, { if (t == selectedType) attr("selected", "selected") }) { Text(biomarkerLabel(t)) }
                    }
                }
            }

            // Value + unit
            Div({ style { display(DisplayStyle.Flex); gap(12.px); marginBottom(20.px); alignItems(AlignItems.FlexEnd) }}) {
                Div({ style { flex(1) }}) {
                    Label { Span({ classes("bm-label") }) { Text("Value") } }
                    Input(org.jetbrains.compose.web.attributes.InputType.Number) {
                        classes("bm-input")
                        value(valueInput)
                        onInput { valueInput = it.value.toString() }
                        attr("placeholder", "e.g. 95")
                        attr("step", "0.1")
                        attr("min", "0")
                    }
                }
                Div({ style {
                    padding(11.px, 14.px); backgroundColor(Color(BM_SURFACE)); borderRadius(10.px)
                    border(1.5.px, LineStyle.Solid, Color(BM_BORDER))
                    fontSize(14.px); fontWeight("700"); color(Color(BM_INK_MID))
                    fontFamily("DM Sans, sans-serif"); whiteSpace("nowrap")
                }}) { Text(unitFor[selectedType] ?: "unit") }
            }

            // Status toggle
            Div({ style { marginBottom(20.px) }}) {
                Span({ classes("bm-label") }) { Text("Status") }
                Div({ style { display(DisplayStyle.Flex); gap(10.px); marginTop(6.px) }}) {
                    listOf(true to "Normal", false to "Attention Required").forEach { (value, label) ->
                        Button({
                            style {
                                padding(8.px, 18.px); borderRadius(999.px); border(1.5.px, LineStyle.Solid, Color(
                                if (isNormal == value) (if (value) BM_TEAL else BM_WARN) else BM_BORDER
                            ))
                                backgroundColor(Color(
                                    if (isNormal == value) (if (value) BM_TEAL else BM_WARN) else BM_WHITE
                                ))
                                color(if (isNormal == value) Color.white else Color(BM_INK_MID))
                                fontSize(13.px); fontWeight("700"); cursor("pointer")
                                fontFamily("DM Sans, sans-serif")
                            }
                            onClick { isNormal = value }
                        }) { Text(label) }
                    }
                }
            }

            // Notes
            Div({ style { marginBottom(24.px) }}) {
                Label { Span({ classes("bm-label") }) { Text("Notes (optional)") } }
                TextArea(attrs = {
                    classes("bm-input")
                    style { height(72.px); property("resize","vertical") }
                    value(notesInput)
                    onInput { notesInput = it.value }
                })
            }

            // Error
            if (error != null) {
                Div({ style {
                    fontSize(13.px); color(Color(BM_DANGER)); marginBottom(16.px)
                    fontFamily("DM Sans, sans-serif")
                }}) { Text(error!!) }
            }

            // Footer actions
            Div({ style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd) }}) {
                Button({ classes("bm-btn-ghost"); onClick { onClose() } }) { Text("Cancel") }
                Button({
                    classes("bm-btn-primary")
                    onClick {
                        val v = valueInput.toDoubleOrNull()
                        when {
                            v == null || valueInput.isBlank() ->
                                error = "Please enter a valid numeric value."
                            v < 0 ->
                                error = "Value must be positive."
                            else -> {
                                error = null
                                onAdded(Biomarker(
                                    biomarkerId   = "bm_${kotlin.js.Date.now().toLong()}",
                                    patientId     = patientId,
                                    biomarkerType = selectedType,
                                    value         = v,
                                    unit          = unitFor[selectedType] ?: "",
                                    timestamp     = kotlin.js.Date.now().toLong(),
                                    notes         = notesInput.trim(),
                                    isNormal      = isNormal
                                ))
                            }
                        }
                    }
                }) { Text("Save Reading") }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

internal fun biomarkerLabel(type: String) = when (type) {
    "glucose"      -> "Blood Glucose"
    "cholesterol"  -> "Cholesterol"
    "vitamin_d"    -> "Vitamin D"
    "hemoglobin"   -> "Hemoglobin"
    "vitamin_b12"  -> "Vitamin B12"
    else           -> type.replace("_"," ").replaceFirstChar { it.uppercase() }
}

internal fun biomarkerReferenceRange(type: String) = when (type) {
    "glucose"     -> "Fasting: 70–100 mg/dL (Normal) · 100–125 (Pre-diabetes) · ≥126 (Diabetes)"
    "cholesterol" -> "Total: <200 mg/dL (Desirable) · 200–239 (Borderline) · ≥240 (High)"
    "vitamin_d"   -> "Optimal: 30–100 ng/mL · Insufficient: 20–29 · Deficient: <20"
    "hemoglobin"  -> "Men: 13.5–17.5 g/dL · Women: 12–15.5 g/dL"
    "vitamin_b12" -> "Normal: 200–900 pg/mL · Deficient: <200 pg/mL"
    else          -> "Consult reference lab values"
}

// Single shared date formatter — no duplication across files
internal fun bmFormatDate(timestamp: Long, short: Boolean = false): String {
    val d = kotlin.js.Date(timestamp)
    val day   = d.getDate().toString().padStart(2, '0')
    val month = (d.getMonth() + 1).toString().padStart(2, '0')
    val year  = d.getFullYear()
    return if (short) "$day/$month" else "$day/$month/$year"
}